import { useNavigate } from "react-router-dom";
import { MapPin, Calendar, Building2, User, Briefcase, AlertTriangle, TrendingUp, DollarSign, Eye, Pencil } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Workforce, isEndingSoon, getDaysUntilEnd, calculateMargin } from "@/types/workforce";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface WorkforceCardProps {
	workforce: Workforce;
	className?: string;
	disableLinks?: boolean;
	onEdit?: (workforceId: string) => void;
}

const getStatusColor = (status: string) => {
	return 'text-muted-foreground';
};

const getStatusBadgeVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
	switch (status) {
		case 'active':
			return 'default';
		case 'on_hold':
			return 'secondary';
		case 'completed':
			return 'outline';
		case 'terminated':
			return 'destructive';
		default:
			return 'secondary';
	}
};

const formatStatus = (status: string) => {
	return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

export const WorkforceCard = ({ workforce, className, disableLinks = false, onEdit }: WorkforceCardProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const candidateName = workforce.candidate?.name || 'Unknown Consultant';
	const companyName = workforce.job?.company?.name || workforce.external_company_name || 'Unknown Company';
	const jobTitle = workforce.job?.title || workforce.external_job_title || 'Unknown Role';

	const workforceUrl = `/workforce/${workforce.id}`;

	// Calculate if ending soon (within 30 days) and days remaining
	const endingSoon = workforce.status === 'active' && isEndingSoon(workforce.expected_end_date || workforce.end_date, 30);
	const daysUntilEnd = getDaysUntilEnd(workforce.expected_end_date || workforce.end_date);
	const isUrgent = daysUntilEnd !== null && daysUntilEnd <= 7;

	// Calculate margin
	const dailyMargin = calculateMargin(workforce.purchase_price_daily, workforce.selling_price_daily);
	const hourlyMargin = calculateMargin(workforce.purchase_price_hourly, workforce.selling_price_hourly);
	const margin = dailyMargin ?? hourlyMargin;

	const formatDate = (dateStr: string | null | undefined) => {
		if (!dateStr) return null;
		try {
			return format(new Date(dateStr), 'MMM d, yyyy');
		} catch {
			return dateStr;
		}
	};

	return (
		<div className={cn(
			"relative flex flex-col h-full overflow-hidden transition-all duration-300 hover:shadow-lg backdrop-blur-sm bg-white/80 border border-white/20 rounded-sm",
			"hover:bg-white/90 hover:scale-[1.02] hover:border-white/30 hover:origin-center",
			"dark:bg-gray-800/80 dark:border-gray-700/20 dark:hover:bg-gray-800/90 dark:hover:border-gray-700/30",
			className,
			"w-full h-full"
		)}>
			{/* Header with Consultant Info */}
			<div className="p-4 border-b border-border flex flex-row justify-between items-start min-h-[60px]">
				<div className="flex items-center gap-3 flex-1 min-w-0">
					<Avatar className="h-10 w-10 flex-shrink-0">
						<AvatarFallback className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
							{candidateName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
						</AvatarFallback>
					</Avatar>
					{disableLinks ? (
						<div className="flex-1 min-w-0">
							<h3 className="text-lg font-semibold truncate text-gray-900 dark:text-gray-100">{candidateName}</h3>
						</div>
					) : workforce.candidate_id ? (
						<a
							href={`/candidates/${workforce.candidate_id}`}
							onClick={(e) => {
								if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
								e.preventDefault();
								navigate(`/candidates/${workforce.candidate_id}`);
							}}
							className="hover:text-primary transition-colors flex-1 min-w-0"
						>
							<h3 className="text-lg font-semibold truncate text-gray-900 dark:text-gray-100">{candidateName}</h3>
						</a>
					) : (
						<div className="flex-1 min-w-0">
							<h3 className="text-lg font-semibold truncate text-gray-900 dark:text-gray-100">{candidateName}</h3>
						</div>
					)}
				</div>
				<Badge variant={getStatusBadgeVariant(workforce.status)} className="ml-2 flex-shrink-0">
					{formatStatus(workforce.status)}
				</Badge>
			</div>

			{/* Ending Soon Warning Banner */}
			{endingSoon && (
				<div className="px-4 py-2 flex items-center gap-2 text-sm bg-muted border-b">
					<AlertTriangle className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
					<span className={cn("font-medium", isUrgent && "font-semibold")}>
						{daysUntilEnd === 0
							? t("workforce.endingToday", "Ending today!")
							: daysUntilEnd === 1
								? t("workforce.endingTomorrow", "Ending tomorrow!")
								: t("workforce.endingSoon", "Ending in {{days}} days", { days: daysUntilEnd })}
					</span>
				</div>
			)}

			{/* Body with Assignment Details */}
			<div className="flex-1 flex flex-col justify-between p-4 gap-3 pb-0">
				{/* Company and Role */}
				<div className="flex flex-col gap-2">
					<div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
						<Building2 className="h-4 w-4 flex-shrink-0" />
						<span className="truncate font-medium">{companyName}</span>
					</div>
					<div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
						<Briefcase className="h-4 w-4 flex-shrink-0" />
						<span className="truncate">{jobTitle}</span>
					</div>
				</div>

				{/* Dates */}
				<div className="flex flex-col gap-1">
					<div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
						<Calendar className="h-4 w-4 flex-shrink-0" />
						<span>
							{formatDate(workforce.start_date)}
							{workforce.end_date ? ` - ${formatDate(workforce.end_date)}` :
							 workforce.expected_end_date ? ` - ${formatDate(workforce.expected_end_date)} (expected)` :
							 ' - Ongoing'}
						</span>
					</div>
				</div>

				{/* Location */}
				{workforce.location && (
					<div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
						<MapPin className="h-4 w-4 flex-shrink-0" />
						<span className="truncate">{workforce.location}</span>
						{workforce.location_type && (
							<Badge variant="outline" className="text-xs">
								{workforce.location_type}
							</Badge>
						)}
					</div>
				)}

				{/* Work Percentage */}
				{workforce.work_percentage && workforce.work_percentage < 100 && (
					<div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
						<User className="h-4 w-4 flex-shrink-0" />
						<span>{workforce.work_percentage}% allocation</span>
					</div>
				)}

				{/* Financial Info - Margin & Rate */}
				{(margin !== null || workforce.selling_price_daily || workforce.selling_price_hourly) && (
					<div className="flex items-center gap-2 text-sm">
						{margin !== null && (
							<Badge variant="secondary" className="flex items-center gap-1">
								<TrendingUp className="h-3 w-3" />
								{margin.toFixed(1)}% margin
							</Badge>
						)}
						{(workforce.selling_price_daily || workforce.selling_price_hourly) && (
							<span className="text-muted-foreground flex items-center gap-1">
								<DollarSign className="h-3 w-3" />
								{workforce.selling_price_daily
									? `${workforce.currency} ${workforce.selling_price_daily}/day`
									: `${workforce.currency} ${workforce.selling_price_hourly}/hour`}
							</span>
						)}
					</div>
				)}
			</div>

			{/* Footer with action icons */}
			<div className="flex justify-end items-center gap-1 p-4 pt-2">
				{!disableLinks && (
					<>
						<Tooltip>
							<TooltipTrigger asChild>
								<button
									className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
									onClick={() => navigate(workforceUrl)}
								>
									<Eye className="h-4 w-4" />
								</button>
							</TooltipTrigger>
							<TooltipContent side="bottom">{t("common.view", "View")}</TooltipContent>
						</Tooltip>
						<Tooltip>
							<TooltipTrigger asChild>
								<button
									className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
									onClick={() => onEdit?.(workforce.id)}
								>
									<Pencil className="h-4 w-4" />
								</button>
							</TooltipTrigger>
							<TooltipContent side="bottom">{t("common.edit", "Edit")}</TooltipContent>
						</Tooltip>
					</>
				)}
			</div>
		</div>
	);
};
